import { registerBlockType } from "@wordpress/blocks";
import {
    InspectorControls,
    useBlockProps,
    PanelColorSettings,
} from "@wordpress/block-editor";
import {
    PanelBody,
    Button,
    TextControl,
    ToggleControl,
    ButtonGroup,
    __experimentalView as View,
    __experimentalText as Text,
} from "@wordpress/components";
import UnitRange from "./components/UnitRange";
import { table, tableColumnAfter, settings } from "@wordpress/icons";

// Domyślna struktura tabeli
const DEFAULT_TABLE = {
    headers: ["Nagłówek 1", "Nagłówek 2", "Nagłówek 3"],
    rows: [
        ["Wiersz 1, Kolumna 1", "Wiersz 1, Kolumna 2", "Wiersz 1, Kolumna 3"],
        ["Wiersz 2, Kolumna 1", "Wiersz 2, Kolumna 2", "Wiersz 2, Kolumna 3"],
    ],
};

// W komponencie edit dodaj nowy komponent wyboru układu
const TableLayoutSelector = ({ tableLayout, onChange }) => {
    return (
        <PanelBody title="Układ tabeli" initialOpen={true}>
            <View
                style={{
                    padding: "16px 0",
                    borderRadius: "4px",
                }}
            >
                <ButtonGroup
                    style={{
                        width: "100%",
                        gap: "8px",
                        display: "grid",
                        gridTemplateColumns: "repeat(2, 1fr)",
                    }}
                >
                    <Button
                        label="Nagłówek w wierszu"
                        isPressed={tableLayout === "row"}
                        onClick={() => onChange("row")}
                        title="Nagłówek w wierszu"
                        style={{
                            boxShadow: "none",
                            justifyContent: "center",
                            border: "1px solid #e2e4e7",
                            borderRadius: "4px",
                            background:
                                tableLayout === "row" ? "#007cba" : "#fff",
                            color: tableLayout === "row" ? "#fff" : "#1e1e1e",
                            padding: "8px 12px",
                            height: "auto",
                            minHeight: "48px",
                            transition: "all 0.2s ease",
                        }}
                    >
                        <View
                            style={{
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "center",
                                gap: "6px",
                            }}
                        >
                            <View
                                style={{
                                    width: "24px",
                                    height: "24px",
                                    opacity: tableLayout === "row" ? 0.9 : 0.7,
                                }}
                            >
                                {table}
                            </View>
                            <Text
                                style={{
                                    fontSize: "12px",
                                    margin: 0,
                                    fontWeight: "500",
                                    color:
                                        tableLayout === "row"
                                            ? "#fff"
                                            : "#1e1e1e",
                                    opacity: tableLayout === "row" ? 1 : 0.9,
                                }}
                            >
                                Wiersz
                            </Text>
                        </View>
                    </Button>
                    <Button
                        label="Nagłówek w kolumnie"
                        isPressed={tableLayout === "column"}
                        onClick={() => onChange("column")}
                        title="Nagłówek w kolumnie"
                        style={{
                            boxShadow: "none",
                            justifyContent: "center",
                            border: "1px solid #e2e4e7",
                            borderRadius: "4px",
                            background:
                                tableLayout === "column" ? "#007cba" : "#fff",
                            color:
                                tableLayout === "column" ? "#fff" : "#1e1e1e",
                            padding: "8px 12px",
                            height: "auto",
                            minHeight: "48px",
                            transition: "all 0.2s ease",
                        }}
                    >
                        <View
                            style={{
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "center",
                                gap: "6px",
                            }}
                        >
                            <View
                                style={{
                                    width: "24px",
                                    height: "24px",
                                    opacity:
                                        tableLayout === "column" ? 0.9 : 0.7,
                                }}
                            >
                                {tableColumnAfter}
                            </View>
                            <Text
                                style={{
                                    fontSize: "12px",
                                    margin: 0,
                                    fontWeight: "500",
                                    color:
                                        tableLayout === "column"
                                            ? "#fff"
                                            : "#1e1e1e",
                                    opacity: tableLayout === "column" ? 1 : 0.9,
                                }}
                            >
                                Kolumna
                            </Text>
                        </View>
                    </Button>
                </ButtonGroup>
            </View>
        </PanelBody>
    );
};

registerBlockType("danone/modern-table", {
    apiVersion: 2,
    title: "Kami - Table",
    icon: "grid-view",
    category: "design",

    attributes: {
        headers: {
            type: "array",
            default: DEFAULT_TABLE.headers,
        },
        rows: {
            type: "array",
            default: DEFAULT_TABLE.rows,
        },
        tableStyles: {
            type: "object",
            default: {
                headerBgColor: "#007cba",
                headerTextColor: "#ffffff",
                cellBgColor: "#ffffff",
                cellTextColor: "#333333",
                borderColor: "#dddddd",
                stripedColor: "rgba(0,0,0,0.02)",
                fontSize: { value: 14, unit: "px" },
                headerFontSize: { value: 16, unit: "px" },
            },
        },
        isStriped: {
            type: "boolean",
            default: true,
        },
        isResponsive: {
            type: "boolean",
            default: true,
        },
        isSortable: {
            type: "boolean",
            default: false,
        },
        tableLayout: {
            type: "string",
            default: "row",
        },
    },

    edit: function ModernTableEdit({ attributes, setAttributes }) {
        const {
            headers,
            rows,
            tableStyles,
            isStriped,
            isResponsive,
            isSortable,
            tableLayout,
        } = attributes;
        const blockProps = useBlockProps();

        // Dodaj funkcję pomocniczą do bezpiecznego dostępu do wartości
        const getStyleValue = (path, defaultValue) => {
            try {
                return (
                    path
                        .split(".")
                        .reduce((obj, key) => obj[key], tableStyles) ??
                    defaultValue
                );
            } catch (e) {
                return defaultValue;
            }
        };

        const addColumn = () => {
            if (tableLayout === "row") {
                // Dodawanie kolumny w układzie wierszowym
                const newHeaders = [
                    ...headers,
                    `Nagłówek ${headers.length + 1}`,
                ];
                const newRows = rows.map((row) => [...row, ""]);
                setAttributes({ headers: newHeaders, rows: newRows });
            } else {
                // Dodawanie kolumny w układzie kolumnowym
                const newHeaders = [
                    ...headers,
                    `Nagłówek ${headers.length + 1}`,
                ];
                const newRows = rows.map((row) => [...row, ""]);
                setAttributes({ headers: newHeaders, rows: newRows });
            }
        };

        const addRow = () => {
            if (tableLayout === "row") {
                // Dodawanie wiersza w układzie wierszowym
                const newRow = new Array(headers.length).fill("");
                setAttributes({ rows: [...rows, newRow] });
            } else {
                // Dodawanie wiersza w układzie kolumnowym
                const newRow = new Array(headers.length).fill("");
                setAttributes({ rows: [...rows, newRow] });
            }
        };

        const removeColumn = (index) => {
            const newHeaders = headers.filter((_, i) => i !== index);
            const newRows = rows.map((row) =>
                row.filter((_, i) => i !== index)
            );
            setAttributes({ headers: newHeaders, rows: newRows });
        };

        const removeRow = (index) => {
            const newRows = rows.filter((_, i) => i !== index);
            setAttributes({ rows: newRows });
        };

        const updateCell = (rowIndex, colIndex, value) => {
            const newRows = [...rows];
            newRows[rowIndex][colIndex] = value;
            setAttributes({ rows: newRows });
        };

        const updateHeader = (index, value) => {
            const newHeaders = [...headers];
            newHeaders[index] = value;
            setAttributes({ headers: newHeaders });
        };

        return (
            <>
                <InspectorControls>
                    <TableLayoutSelector
                        tableLayout={tableLayout}
                        onChange={(value) =>
                            setAttributes({ tableLayout: value })
                        }
                    />
                    <PanelColorSettings
                        title="Kolory"
                        enableAlpha={true}
                        colorSettings={[
                            {
                                value: getStyleValue("cellBgColor", "#ffffff"),
                                onChange: (color) =>
                                    setAttributes({
                                        tableStyles: {
                                            ...tableStyles,
                                            cellBgColor: color,
                                        },
                                    }),
                                label: "Tło komórki",
                            },
                            {
                                value: getStyleValue(
                                    "stripedColor",
                                    "rgba(0,0,0,0.02)"
                                ),
                                onChange: (color) =>
                                    setAttributes({
                                        tableStyles: {
                                            ...tableStyles,
                                            stripedColor: color,
                                        },
                                    }),
                                label: "Kolor pasiastych wierszy",
                            },
                            {
                                value: getStyleValue("borderColor", "#dddddd"),
                                onChange: (color) =>
                                    setAttributes({
                                        tableStyles: {
                                            ...tableStyles,
                                            borderColor: color,
                                        },
                                    }),
                                label: "Kolor obramowania",
                            },
                        ]}
                    />
                    <View
                        style={{
                            padding: "15px",
                        }}
                    >
                        <UnitRange
                            label="Rozmiar czcionki nagłówka"
                            rangeValue={getStyleValue(
                                "headerFontSize.value",
                                16
                            )}
                            onRangeChange={(value) =>
                                setAttributes({
                                    tableStyles: {
                                        ...tableStyles,
                                        headerFontSize: {
                                            ...tableStyles.headerFontSize,
                                            value,
                                        },
                                    },
                                })
                            }
                            unitValue={getStyleValue("headerFontSize", {
                                value: 16,
                                unit: "px",
                            })}
                            onUnitChange={(value) =>
                                setAttributes({
                                    tableStyles: {
                                        ...tableStyles,
                                        headerFontSize: value,
                                    },
                                })
                            }
                            min={12}
                            max={24}
                            style={{ marginBottom: "20px" }}
                        />

                        <UnitRange
                            label="Rozmiar czcionki komórek"
                            rangeValue={getStyleValue("fontSize.value", 14)}
                            onRangeChange={(value) =>
                                setAttributes({
                                    tableStyles: {
                                        ...tableStyles,
                                        fontSize: {
                                            ...tableStyles.fontSize,
                                            value,
                                        },
                                    },
                                })
                            }
                            unitValue={getStyleValue("fontSize", {
                                value: 14,
                                unit: "px",
                            })}
                            onUnitChange={(value) =>
                                setAttributes({
                                    tableStyles: {
                                        ...tableStyles,
                                        fontSize: value,
                                    },
                                })
                            }
                            min={12}
                            max={20}
                            style={{ marginBottom: "20px" }}
                        />

                        <ToggleControl
                            label="Pasiaste wiersze"
                            checked={isStriped}
                            onChange={(value) =>
                                setAttributes({ isStriped: value })
                            }
                            __nextHasNoMarginBottom
                        />

                        <ToggleControl
                            label="Responsywność"
                            checked={isResponsive}
                            onChange={(value) =>
                                setAttributes({ isResponsive: value })
                            }
                            __nextHasNoMarginBottom
                        />

                        <ToggleControl
                            label="Sortowanie"
                            checked={isSortable}
                            onChange={(value) =>
                                setAttributes({ isSortable: value })
                            }
                            __nextHasNoMarginBottom
                        />
                    </View>

                    <PanelBody title="Ustawienia nagłówka" initialOpen={false}>
                        <PanelColorSettings
                            title="Kolory nagłówka"
                            enableAlpha={true}
                            colorSettings={[
                                {
                                    value: getStyleValue(
                                        "headerBgColor",
                                        "#007cba"
                                    ),
                                    onChange: (color) =>
                                        setAttributes({
                                            tableStyles: {
                                                ...tableStyles,
                                                headerBgColor: color,
                                            },
                                        }),
                                    label: "Tło nagłówka",
                                },
                                {
                                    value: getStyleValue(
                                        "headerTextColor",
                                        "#ffffff"
                                    ),
                                    onChange: (color) =>
                                        setAttributes({
                                            tableStyles: {
                                                ...tableStyles,
                                                headerTextColor: color,
                                            },
                                        }),
                                    label: "Tekst nagłówka",
                                },
                            ]}
                        />
                    </PanelBody>
                </InspectorControls>

                <div {...blockProps}>
                    <div
                        className={`modern-table ${
                            isResponsive ? "is-responsive" : ""
                        } layout-${tableLayout}`}
                        style={{
                            "--header-bg-color": getStyleValue(
                                "headerBgColor",
                                "#007cba"
                            ),
                            "--header-text-color": getStyleValue(
                                "headerTextColor",
                                "#ffffff"
                            ),
                            "--cell-bg-color": getStyleValue(
                                "cellBgColor",
                                "#ffffff"
                            ),
                            "--cell-text-color": getStyleValue(
                                "cellTextColor",
                                "#333333"
                            ),
                            "--border-color": getStyleValue(
                                "borderColor",
                                "#dddddd"
                            ),
                            "--border-width": "1px",
                            "--cell-padding": "12px",
                            "--header-border-radius": "0px",
                            "--header-font-size": `${getStyleValue(
                                "headerFontSize.value",
                                16
                            )}${getStyleValue("headerFontSize.unit", "px")}`,
                        }}
                    >
                        <table
                            style={{
                                overflow: "hidden",
                            }}
                        >
                            {tableLayout === "row" ? (
                                // Tradycyjny układ z nagłówkiem w wierszu
                                <>
                                    <thead>
                                        <tr>
                                            {headers.map((header, index) => (
                                                <th
                                                    key={index}
                                                    style={{
                                                        fontSize: `${getStyleValue(
                                                            "headerFontSize.value",
                                                            16
                                                        )}${getStyleValue(
                                                            "headerFontSize.unit",
                                                            "px"
                                                        )}`,
                                                        backgroundColor:
                                                            getStyleValue(
                                                                "headerBgColor",
                                                                "#007cba"
                                                            ),
                                                        color: "var(--header-text-color)",
                                                        borderRadius:
                                                            "var(--header-border-radius)",
                                                        overflow: "hidden",
                                                    }}
                                                >
                                                    <TextControl
                                                        value={header}
                                                        onChange={(value) =>
                                                            updateHeader(
                                                                index,
                                                                value
                                                            )
                                                        }
                                                        __nextHasNoMarginBottom
                                                    />
                                                    <Button
                                                        isSmall
                                                        isDestructive
                                                        onClick={() =>
                                                            removeColumn(index)
                                                        }
                                                        className="table-delete-button"
                                                    >
                                                        Usuń kolumnę
                                                    </Button>
                                                </th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {rows.map((row, rowIndex) => (
                                            <tr
                                                key={rowIndex}
                                                style={{
                                                    backgroundColor:
                                                        isStriped &&
                                                        rowIndex % 2
                                                            ? getStyleValue(
                                                                  "stripedColor",
                                                                  "rgba(0,0,0,0.02)"
                                                              )
                                                            : "transparent",
                                                }}
                                            >
                                                {row.map((cell, colIndex) => (
                                                    <td
                                                        key={colIndex}
                                                        style={{
                                                            fontSize: `${getStyleValue(
                                                                "fontSize.value",
                                                                14
                                                            )}${getStyleValue(
                                                                "fontSize.unit",
                                                                "px"
                                                            )}`,
                                                        }}
                                                    >
                                                        <TextControl
                                                            value={cell}
                                                            onChange={(value) =>
                                                                updateCell(
                                                                    rowIndex,
                                                                    colIndex,
                                                                    value
                                                                )
                                                            }
                                                            __nextHasNoMarginBottom
                                                        />
                                                    </td>
                                                ))}
                                                <td>
                                                    <Button
                                                        isSmall
                                                        isDestructive
                                                        onClick={() =>
                                                            removeRow(rowIndex)
                                                        }
                                                        className="table-delete-button"
                                                    >
                                                        Usuń wiersz
                                                    </Button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </>
                            ) : (
                                // Układ z nagłówkiem w kolumnie
                                <tbody>
                                    {headers.map((header, index) => (
                                        <tr key={index}>
                                            <th
                                                style={{
                                                    fontSize: `${getStyleValue(
                                                        "headerFontSize.value",
                                                        16
                                                    )}${getStyleValue(
                                                        "headerFontSize.unit",
                                                        "px"
                                                    )}`,
                                                    backgroundColor:
                                                        getStyleValue(
                                                            "headerBgColor",
                                                            "#007cba"
                                                        ),
                                                    color: "var(--header-text-color)",
                                                    borderRadius:
                                                        "var(--header-border-radius)",
                                                    overflow: "hidden",
                                                }}
                                            >
                                                <TextControl
                                                    value={header}
                                                    onChange={(value) =>
                                                        updateHeader(
                                                            index,
                                                            value
                                                        )
                                                    }
                                                    __nextHasNoMarginBottom
                                                />
                                            </th>
                                            {rows.map((row, rowIndex) => (
                                                <td
                                                    key={rowIndex}
                                                    style={{
                                                        fontSize: `${getStyleValue(
                                                            "fontSize.value",
                                                            14
                                                        )}${getStyleValue(
                                                            "fontSize.unit",
                                                            "px"
                                                        )}`,
                                                        backgroundColor:
                                                            isStriped &&
                                                            rowIndex % 2
                                                                ? getStyleValue(
                                                                      "stripedColor",
                                                                      "rgba(0,0,0,0.02)"
                                                                  )
                                                                : "transparent",
                                                    }}
                                                >
                                                    <TextControl
                                                        value={row[index]}
                                                        onChange={(value) =>
                                                            updateCell(
                                                                rowIndex,
                                                                index,
                                                                value
                                                            )
                                                        }
                                                        __nextHasNoMarginBottom
                                                    />
                                                </td>
                                            ))}
                                            <td
                                                style={{
                                                    background: "transparent",
                                                }}
                                            >
                                                <Button
                                                    isSmall
                                                    isDestructive
                                                    onClick={() =>
                                                        removeColumn(index)
                                                    }
                                                    className="table-delete-button"
                                                >
                                                    Usuń wiersz
                                                </Button>
                                            </td>
                                        </tr>
                                    ))}
                                    <tr>
                                        <th
                                            style={{
                                                background: "transparent",
                                            }}
                                        ></th>
                                        {rows.map((_, rowIndex) => (
                                            <td
                                                key={rowIndex}
                                                style={{
                                                    background: "transparent",
                                                }}
                                            >
                                                <Button
                                                    isSmall
                                                    isDestructive
                                                    onClick={() =>
                                                        removeRow(rowIndex)
                                                    }
                                                    className="table-delete-button"
                                                >
                                                    Usuń kolumnę
                                                </Button>
                                            </td>
                                        ))}
                                        <td
                                            style={{
                                                background: "transparent",
                                            }}
                                        ></td>
                                    </tr>
                                </tbody>
                            )}
                        </table>

                        <div
                            className="table-controls"
                            style={{
                                marginTop: "20px",
                                display: "flex",
                                gap: "8px",
                            }}
                        >
                            <Button
                                onClick={
                                    tableLayout === "row" ? addRow : addColumn
                                }
                                className="table-add-button"
                            >
                                Dodaj wiersz
                            </Button>
                            <Button
                                onClick={
                                    tableLayout === "row" ? addColumn : addRow
                                }
                                className="table-add-button"
                            >
                                Dodaj kolumnę
                            </Button>
                        </div>
                    </div>
                </div>
            </>
        );
    },

    save: function ModernTableSave({ attributes }) {
        const {
            headers,
            rows,
            tableStyles,
            isStriped,
            isResponsive,
            isSortable,
            tableLayout,
        } = attributes;
        const blockProps = useBlockProps.save();

        // Dodaj funkcję pomocniczą do bezpiecznego dostępu do wartości
        const getStyleValue = (path, defaultValue) => {
            try {
                return (
                    path
                        .split(".")
                        .reduce((obj, key) => obj[key], tableStyles) ??
                    defaultValue
                );
            } catch (e) {
                return defaultValue;
            }
        };

        return (
            <div {...blockProps}>
                <div
                    className={`modern-table ${
                        isResponsive ? "is-responsive" : ""
                    } layout-${tableLayout}`}
                    data-sortable={isSortable}
                    style={{
                        "--header-bg-color": getStyleValue(
                            "headerBgColor",
                            "#007cba"
                        ),
                        "--header-text-color": getStyleValue(
                            "headerTextColor",
                            "#ffffff"
                        ),
                        "--cell-bg-color": getStyleValue(
                            "cellBgColor",
                            "#ffffff"
                        ),
                        "--cell-text-color": getStyleValue(
                            "cellTextColor",
                            "#333333"
                        ),
                        "--border-color": getStyleValue(
                            "borderColor",
                            "#dddddd"
                        ),
                        "--border-width": "1px",
                        "--cell-padding": "12px",
                        "--header-border-radius": "0px",
                        "--header-font-size": `${getStyleValue(
                            "headerFontSize.value",
                            16
                        )}${getStyleValue("headerFontSize.unit", "px")}`,
                    }}
                >
                    <table
                        style={{
                            overflow: "hidden",
                        }}
                    >
                        {tableLayout === "row" ? (
                            <>
                                <thead>
                                    <tr>
                                        {headers.map((header, index) => (
                                            <th
                                                key={index}
                                                style={{
                                                    fontSize: `${getStyleValue(
                                                        "headerFontSize.value",
                                                        16
                                                    )}${getStyleValue(
                                                        "headerFontSize.unit",
                                                        "px"
                                                    )}`,
                                                    backgroundColor:
                                                        getStyleValue(
                                                            "headerBgColor",
                                                            "#007cba"
                                                        ),
                                                    color: "var(--header-text-color)",
                                                    borderRadius:
                                                        "var(--header-border-radius)",
                                                    overflow: "hidden",
                                                }}
                                            >
                                                {header}
                                            </th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {rows.map((row, rowIndex) => (
                                        <tr
                                            key={rowIndex}
                                            style={{
                                                backgroundColor:
                                                    isStriped && rowIndex % 2
                                                        ? getStyleValue(
                                                              "stripedColor",
                                                              "rgba(0,0,0,0.02)"
                                                          )
                                                        : "transparent",
                                            }}
                                        >
                                            {row.map((cell, colIndex) => (
                                                <td
                                                    key={colIndex}
                                                    style={{
                                                        fontSize: `${getStyleValue(
                                                            "fontSize.value",
                                                            14
                                                        )}${getStyleValue(
                                                            "fontSize.unit",
                                                            "px"
                                                        )}`,
                                                    }}
                                                >
                                                    {cell}
                                                </td>
                                            ))}
                                        </tr>
                                    ))}
                                </tbody>
                            </>
                        ) : (
                            <tbody>
                                {headers.map((header, index) => (
                                    <tr key={index}>
                                        <th
                                            style={{
                                                fontSize: `${getStyleValue(
                                                    "headerFontSize.value",
                                                    16
                                                )}${getStyleValue(
                                                    "headerFontSize.unit",
                                                    "px"
                                                )}`,
                                                backgroundColor: getStyleValue(
                                                    "headerBgColor",
                                                    "#007cba"
                                                ),
                                                color: "var(--header-text-color)",
                                                borderRadius:
                                                    "var(--header-border-radius)",
                                                overflow: "hidden",
                                            }}
                                        >
                                            {header}
                                        </th>
                                        {rows.map((row, rowIndex) => (
                                            <td
                                                key={rowIndex}
                                                style={{
                                                    fontSize: `${getStyleValue(
                                                        "fontSize.value",
                                                        14
                                                    )}${getStyleValue(
                                                        "fontSize.unit",
                                                        "px"
                                                    )}`,
                                                    backgroundColor:
                                                        isStriped &&
                                                        rowIndex % 2
                                                            ? getStyleValue(
                                                                  "stripedColor",
                                                                  "rgba(0,0,0,0.02)"
                                                              )
                                                            : "transparent",
                                                }}
                                            >
                                                {row[index]}
                                            </td>
                                        ))}
                                    </tr>
                                ))}
                            </tbody>
                        )}
                    </table>
                </div>
            </div>
        );
    },
});
